import React, { useState, useEffect } from 'react';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';

const BannerPage = () => {
  const [banners, setBanners] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [formValues, setFormValues] = useState({
    images: null,
    link: '',
    active: false,
    startDate: '',
    endDate: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);  

  useEffect(() => {
    fetchBanners(currentPage, searchQuery);
  }, [currentPage, searchQuery]);

  const fetchBanners = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const res = await axios.get(`/admin/api/banners?page=${page}&limit=${limit}&search=${search}`);
      console.log('API Response:', res.data); // Debug log
      
      // Handle different response structures
      let bannersData = [];
      let totalCount = 0;
      
      if (res.data.banners) {
        // Standard paginated response
        bannersData = res.data.banners;
        totalCount = res.data.totalCount || res.data.banners.length;
      } else if (res.data.banner) {
        // Single banner response
        bannersData = [res.data.banner];
        totalCount = 1;
      } else if (Array.isArray(res.data)) {
        // Direct array response
        bannersData = res.data;
        totalCount = res.data.length;
      } else {
        // Fallback
        bannersData = [];
        totalCount = 0;
      }
      
      console.log('Processed banners:', bannersData);
      setBanners(bannersData);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      console.error('Failed to fetch Banners:', err);
      console.error('Error details:', err.response?.data);
      toastError('Failed to fetch banners');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      images: { required: !editingId }, // Image required only for new banners
      link: { required: true },
      startDate: { required: true },
      endDate: { required: true },
    });

    // Additional validation for file size and type
    if (formValues.images instanceof File) {
      const maxSize = 5 * 1024 * 1024; // 5MB
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      
      if (formValues.images.size > maxSize) {
        validationErrors.images = 'Image size should be less than 5MB';
      }
      
      if (!allowedTypes.includes(formValues.images.type)) {
        validationErrors.images = 'Only JPEG, PNG, GIF, and WebP images are allowed';
      }
    }

    // Date validation
    if (formValues.startDate && formValues.endDate) {
      const startDate = new Date(formValues.startDate);
      const endDate = new Date(formValues.endDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (startDate < today) {
        validationErrors.startDate = 'Start date cannot be in the past';
      }
      
      if (endDate <= startDate) {
        validationErrors.endDate = 'End date must be after start date';
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      
      if (formValues.images instanceof File) {
        formData.append('images', formValues.images);
      }
      
      // Match exact API request format
      formData.append('active', formValues.active);
      formData.append('link', formValues.link || '');
      formData.append('startDate', formValues.startDate || '');
      formData.append('endDate', formValues.endDate || '');

      console.log('Sending form data (API format):');
      console.log('- Image file:', formValues.images instanceof File ? formValues.images.name : 'No file');
      console.log('- active:', formValues.active);
      console.log('- link:', formValues.link);
      console.log('- startDate:', formValues.startDate);
      console.log('- endDate:', formValues.endDate);
      
      // Debug: Log all form values before sending
      console.log('Complete form values:', {
        images: formValues.images instanceof File ? formValues.images.name : formValues.images,
        active: formValues.active,
        link: formValues.link,
        startDate: formValues.startDate,
        endDate: formValues.endDate
      });

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      let response;
      if (editingId) {
        response = await axios.put(`/admin/api/banners/${editingId}`, formData, config);
        toastSuccess('Slider updated successfully!');
      } else {
        response = await axios.post('/admin/api/banners', formData, config);
        toastSuccess('Slider added successfully!');
      }
      
      console.log('Save response:', response.data);
      
      // Handle response format consistently with fetchBanners
      if (response.data) {
        if (response.data.Message) {
          console.log('Saved banner data:', response.data.banner);
        } else if (response.data.Message) {
          console.log('Saved banners data:', response.data.banners);
        } else if (response.data.Message) {
          console.log('Save message:', response.data.message);
        }
      }
      
      setCurrentPage(1);
      fetchBanners(1, searchQuery);
      handleCancel();
    } catch (err) {
      console.error('Failed to save Banner:', err);
      toastError(err.response?.data?.message || 'Failed to save Banner');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (banner) => {
    setFormValues({
      images: null, // Don't preload the image file for editing
      link: banner.link || '',
      active: banner.active || false,
      startDate: banner.startDate ? banner.startDate.split('T')[0] : '',
      endDate: banner.endDate ? banner.endDate.split('T')[0] : '',
    });
    setImagePreview(banner.images || '');
    setEditingId(banner._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this banner?')) {
      try {
        await axios.delete(`/admin/api/banners/${id}`);
        toastSuccess('Slider deleted successfully!');
        setCurrentPage(1);
        fetchBanners(1, searchQuery);
      } catch (err) {
        console.error('Failed to delete Banner:', err);
        toastError('Failed to delete Slider');
      }
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({ 
      images: null, 
      link: '',
      active: false,
      startDate: '',
      endDate: '',
    });
    setImagePreview('');
    setEditingId(null);
    setErrors({});
  };

  const handleImageChange = (name, file) => {
    setFormValues(prevValues => ({ ...prevValues, [name]: file }));
    if (file) {
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImagePreview('');
    }
  };

  const handleViewBanner = (banner) => {
    setSelectedBanner(banner);
    setShowViewModal(true);
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const formData = new FormData();
      formData.append('active', !currentStatus);
      
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      const response = await axios.put(`/admin/api/banners/${id}`, formData, config);
      console.log('Toggle status response:', response.data);
      
      // Handle response format consistently
      if (response.data) {
        if (response.data.banner) {
          console.log('Updated banner data:', response.data.banner);
        } else if (response.data.message) {
          console.log('Toggle status message:', response.data.message);
        }
      }
      
      toastSuccess(`Slider ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
      fetchBanners(currentPage, searchQuery);
    } catch (err) {
      console.error('Failed to update slider status:', err);
      console.error('Toggle status error details:', err.response?.data);
      toastError(err.response?.data?.message || 'Failed to update slider status');
    }
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Slider</h3>
        <input
          type="text"
          className="form-control w-25"
          placeholder="Search slider..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">slider List</h5>
          <button
            className="btn btn-sm"
            style={{ backgroundColor: '#C44C23', color: '#fff' }}
            onClick={() => {
              setFormValues({
                images: null,
                link: '',
                active: false,
                startDate: '',
                endDate: '',
              });
              setImagePreview('');
              setEditingId(null);
              setErrors({});
              setShowModal(true);
            }}
          >
            + Add New Slider
          </button>
        </div>

        <div className="card-body p-0">
          <table
            className="table table-bordered table-sm mb-0"
            style={{ fontSize: '14px', lineHeight: '2' }}
          >
            <thead className="table-light">
              <tr>
                <th className="py-1 px-2 w-5">Sr no.</th>
                <th className="py-1 px-2">Image</th>
                <th className="py-1 px-2">Link</th>
                <th className="py-1 px-2">Start Date</th>
                <th className="py-1 px-2">End Date</th>
                <th className="py-1 px-2">Status</th>
                <th className="py-1 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : banners.length > 0 ? (
                banners.map((banner, idx) => (
                  <tr key={banner._id}>
                    <td className="py-1 px-2">{(currentPage - 1) * limit + idx + 1}</td>
                    <td className="py-1 px-2">
                      {banner.images && banner.images.trim() !== '' ? (
                        <img 
                          src={banner.images} 
                          alt="Banner" 
                          style={{ 
                            width: '60px', 
                            height: '40px', 
                            objectFit: 'cover',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(banner.images, '_blank')}
                          title="Click to view full image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'inline';
                          }}
                        />
                      ) : null}
                      {(!banner.images || banner.images.trim() === '') && (
                        <span className="text-muted">No Image</span>
                      )}
                    </td>
                    <td className="py-1 px-2">
                      {banner.link ? (
                        <a 
                          href={banner.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary text-decoration-none"
                          style={{ fontSize: '12px' }}
                          title={banner.link}
                        >
                          {banner.link.length > 30 ? banner.link.substring(0, 30) + '...' : banner.link}
                        </a>
                      ) : (
                        <span className="text-muted">No Link</span>
                      )}
                    </td>
                    <td className="py-1 px-2">
                      {banner.startDate ? (
                        <span style={{ fontSize: '12px' }}>
                          {new Date(banner.startDate).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="py-1 px-2">
                      {banner.endDate ? (
                        <span style={{ fontSize: '12px' }}>
                          {new Date(banner.endDate).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="py-1 px-2">
                      <span 
                        className={`badge ${banner.active ? 'badge-success' : 'badge-secondary'}`}
                        style={{
                          backgroundColor: banner.active ? '#28a745' : '#6c757d',
                          color: 'white',
                          fontSize: '11px',
                          padding: '4px 8px',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleToggleStatus(banner._id, banner.active)}
                        title={`Click to ${banner.active ? 'deactivate' : 'activate'}`}
                      >
                        {banner.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-1 px-2">
                      <button
                        className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0 text-primary-custom"
                        title="Edit"
                        onClick={() => handleEdit(banner)}
                      >
                        <FaEdit size={14} />
                      </button>
                      {/* <button
                                              className="btn btn-sm py-0 px-2 fs-6 lh-sm shadow-none border-0 text-danger"
                                              title="Delete"
                                              onClick={() => handleDelete(banner._id)}
                                            >
                                              <FaTrash size={14} />
                                            </button> */}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-2">No Banners found.</td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &laquo;
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className="{`btn btn-sm page-btn ${currentPage === page ? 'active' : ''}`}"
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Custom Banner Form Modal */}
      {showModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Slider' : 'Add Slider'}</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={handleCancel}
                ></button>
              </div>
              <div className="modal-body">
                <form>
                  {/* Image Upload */}
                  <div className="mb-3">
                    <label className="form-label">Slider Image <span className="text-danger">*</span></label>
                    <input
                      type="file"
                      className={`form-control ${errors.images ? 'is-invalid' : ''}`}
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        console.log('Image selected:', file);
                        if (file) {
                          setFormValues(prev => ({ ...prev, images: file }));
                          setImagePreview(URL.createObjectURL(file));
                          if (errors.images) {
                            setErrors(prev => ({ ...prev, images: '' }));
                          }
                        }
                      }}
                    />
                    {errors.images && <div className="invalid-feedback">{errors.images}</div>}
                    {imagePreview && (
                      <div className="mt-2">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover' }}
                          className="img-thumbnail"
                        />
                      </div>
                    )}
                  </div>

                  {/* Banner Link */}
                  <div className="mb-3">
                    <label className="form-label">Slider Link <span className="text-danger">*</span></label>
                    <input
                      type="url"
                      className={`form-control ${errors.link ? 'is-invalid' : ''}`}
                      placeholder="Enter Slider URL (e.g., https://example.com)"
                      value={formValues.link}
                      onChange={(e) => {
                        console.log('Link changed:', e.target.value);
                        setFormValues(prev => ({ ...prev, link: e.target.value }));
                        if (errors.link) {
                          setErrors(prev => ({ ...prev, link: '' }));
                        }
                      }}
                    />
                    {errors.link && <div className="invalid-feedback">{errors.link}</div>}
                  </div>

                  <div className="row">
                    {/* Start Date */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Start Date <span className="text-danger">*</span></label>
                      <input
                        type="date"
                        className={`form-control ${errors.startDate ? 'is-invalid' : ''}`}
                        value={formValues.startDate}
                        onChange={(e) => {
                          console.log('Start date changed:', e.target.value);
                          setFormValues(prev => ({ ...prev, startDate: e.target.value }));
                          if (errors.startDate) {
                            setErrors(prev => ({ ...prev, startDate: '' }));
                          }
                        }}
                      />
                      {errors.startDate && <div className="invalid-feedback">{errors.startDate}</div>}
                    </div>

                    {/* End Date */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">End Date <span className="text-danger">*</span></label>
                      <input
                        type="date"
                        className={`form-control ${errors.endDate ? 'is-invalid' : ''}`}
                        value={formValues.endDate}
                        onChange={(e) => {
                          console.log('End date changed:', e.target.value);
                          setFormValues(prev => ({ ...prev, endDate: e.target.value }));
                          if (errors.endDate) {
                            setErrors(prev => ({ ...prev, endDate: '' }));
                          }
                        }}
                      />
                      {errors.endDate && <div className="invalid-feedback">{errors.endDate}</div>}
                    </div>
                  </div>

                  {/* Active Status */}
                  <div className="mb-3">
                    <label className="form-label d-block">Active Status</label>
                    <button
                      type="button"
                      className={`btn btn-sm ${formValues.active ? 'btn-success' : 'btn-secondary'}`}
                      style={{ minWidth: 80 }}
                      onClick={() => setFormValues(prev => ({ ...prev, active: !prev.active }))}
                    >
                      {formValues.active ? 'Active' : 'Inactive'}
                    </button>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn"
                  style={{ backgroundColor: '#C44C23', color: 'white' }}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (editingId ? 'Updating...' : 'Saving...') : (editingId ? 'Update' : 'Save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Banner Modal */}
      {showViewModal && selectedBanner && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Banner Preview</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowViewModal(false)}
                ></button>
              </div>
              <div className="modal-body text-center">
                <div className="mb-3">
                  {selectedBanner.images && selectedBanner.images.trim() !== '' ? (
                    <img
                      src={selectedBanner.images}
                      alt="Banner"
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '500px',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : (
                    <div 
                      className="d-flex align-items-center justify-content-center bg-light"
                      style={{ 
                        width: '100%', 
                        height: '300px',
                        border: '2px dashed #ccc',
                        borderRadius: '8px'
                      }}
                    >
                      <span className="text-muted">No Image Available</span>
                    </div>
                  )}
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Status:</strong> 
                      <span className={`ms-2 badge ${selectedBanner.active ? 'badge-success' : 'badge-secondary'}`}>
                        {selectedBanner.active ? 'Active' : 'Inactive'}
                      </span>
                    </p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>Created:</strong> {new Date(selectedBanner.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12 mb-2">
                    <p><strong>Link:</strong> 
                      {selectedBanner.link ? (
                        <a href={selectedBanner.link} target="_blank" rel="noopener noreferrer" className="ms-2 text-primary">
                          {selectedBanner.link}
                        </a>
                      ) : (
                        <span className="ms-2 text-muted">No link provided</span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Start Date:</strong> 
                      <span className="ms-2">
                        {selectedBanner.startDate ? new Date(selectedBanner.startDate).toLocaleDateString() : 'Not set'}
                      </span>
                    </p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>End Date:</strong> 
                      <span className="ms-2">
                        {selectedBanner.endDate ? new Date(selectedBanner.endDate).toLocaleDateString() : 'Not set'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </button>
                {selectedBanner.link && (
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => window.open(selectedBanner.link, '_blank')}
                  >
                    Visit Link
                  </button>
                )}
                {selectedBanner.images && selectedBanner.images.trim() !== '' && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => window.open(selectedBanner.images, '_blank')}
                  >
                    Open Image
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerPage;
      